import { useEffect, useMemo, useRef, useState } from 'react';

import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import type { PetitionDetailsPageUserData } from '#src/app/pages/petitionDetails/pageContext';
import { usePetitionDetailsPageData, usePetitionDetailsPageUserData } from '#src/app/pages/petitionDetails/pageContext';
import { treatExperiment } from '#src/app/shared/api/experiments';
import { useTrackExperimentExposure } from '#src/app/shared/hooks/tracking';

import { useSingleAddressFieldForAuthenticatedAsync } from '../signForm/useSingleAddressFieldForAuthenticatedAsync';
import { useSingleAddressFieldForUnauthenticated } from '../signForm/useSingleAddressFieldForUnauthenticated';
import { useAddressPlaceholderExperiment } from '../useAddressPlaceholderExperiment';
import { useSignFormStep } from '../useSignFormStep';

type AddressPlaceholderExperiment = PetitionDetailsPageUserData['experiments']['signFormAddressPlaceholderExperiment'];

/**
 * Passed to `useTrackExperimentExposure` (which must be called unconditionally, per Rules of Hooks) while
 * the visitor is unqualified — `treated: false` keeps it a no-op.
 */
const INERT_EXPERIMENT = {
	type: 'experiment-service',
	experimentName: 'sign_form_address_placeholder_experiment',
	variation: 'control',
	config: {},
	treated: false,
} as AddressPlaceholderExperiment;

/**
 * Treats `sign_form_address_placeholder_experiment` and fires `$exposure` once the visitor reaches the
 * address step. Fires for every arm — `control` and `fake_control` included, since `qualified` is computed
 * identically in all four.
 *
 * Mount this exactly once, from a location that renders in every arm. It must NOT go inside the sign form:
 * the desktop sidebar, the mobile embedded form and the mobile sign modal each mount their own, and
 * exposure dedup is per mount, so a second instance would halve every measured rate.
 */
export function useTreatAddressPlaceholderExperiment(): void {
	const utilityContext = useUtilityContext();
	const {
		data: {
			petition: { status },
		},
	} = usePetitionDetailsPageData();
	const {
		data: { decisionMaker },
	} = usePetitionDetailsPageUserData();
	const { experiment, isRealAssignment, petitionId } = useAddressPlaceholderExperiment();
	const {
		data: { enabled: singleAddressFieldForUnauthenticated },
	} = useSingleAddressFieldForUnauthenticated();
	const {
		data: { enabled: singleAddressFieldForAuthenticated },
	} = useSingleAddressFieldForAuthenticatedAsync();
	const { signFormStep } = useSignFormStep();

	const [treatedPetitionId, setTreatedPetitionId] = useState<string | null>(null);
	// Ref, not state: a second synchronous pass in the same tick would still read state's pre-update value.
	// Holds *which* petition was treated rather than a boolean, because SPA petition-to-petition navigation
	// reuses this component without unmounting — a boolean would stay true and block the next petition.
	const treatRequestedForRef = useRef<string | null>(null);

	/**
	 * Every sign-form entry point — `Side` and `MobileActionButton` — refuses to render on a closed or
	 * victorious petition, and to a decision maker, so those visitors never reach the address step in any
	 * arm and treating them would only pad the denominator.
	 */
	const signFormReachable = !decisionMaker && status !== 'CLOSED' && status !== 'VICTORY';

	/**
	 * The conditions that put the Google-autocomplete address field on screen: one of the two
	 * single-address flows renders it, and it lives on step 2 behind the step-1 Continue tap.
	 *
	 * The two flows are deliberately OR'd to mirror `useAddressOptions.isShown`, which is what actually
	 * decides whether `LocalizationFields` renders `AddressField`. Checking only the unauthenticated half
	 * would leave AUTHENTICATED visitors with an incomplete address seeing a treatment they were never
	 * treated or exposed for — the arms would diverge visually for a population missing from the analysis.
	 * These two hooks are the same ones `useAddressOptions` composes, so the populations cannot drift.
	 *
	 * The authenticated half is read through its `…Async` variant because this hook mounts in
	 * `components/Main`, which renders during SSR with user data disabled — where the sync session
	 * accessors throw.
	 *
	 * The step gate is what aligns the treated population with the primary metric's denominator — the
	 * metric is `sign_post` per `sign_intent_continue`, so exposing at page load would count visitors who
	 * never tapped Continue. `useSignFormStep` reads page-level state, so it is observable from here
	 * without reaching into a sign-form instance.
	 *
	 * `signFormStep` returning to 1 flips this back to false, but that cannot double-fire: the treat call
	 * is guarded by the petition-keyed ref below, and `useTrackExperimentExposure` dedupes on its own.
	 *
	 * The step gate also can't silently drop traffic: both flows that render this field are two-step.
	 * `useUnauthenticatedSignForm` returns the unauthenticated hook's `enabled` and gates the two-step
	 * `UnauthenticatedSignForm`, and the authenticated half is only reachable through
	 * `TwoStepAuthenticatedSignForm` — so the one-step `GuestSignForm` never shows this field.
	 */
	const qualified =
		signFormReachable &&
		(singleAddressFieldForUnauthenticated || singleAddressFieldForAuthenticated) &&
		signFormStep === 2;

	useEffect(() => {
		if (!qualified || !isRealAssignment || treatRequestedForRef.current === petitionId) return;
		treatRequestedForRef.current = petitionId;
		void (async () => {
			try {
				// treatExperiment resolves `false` rather than throwing when the treatment was not registered
				// server-side — a bot session, or an assignment that isn't treatable yet. Firing `$exposure` on
				// that would report a cohort member the experiment service never recorded, so only a `true`
				// marks this petition treated. A `false` is terminal for this petition: the ref above is already
				// set, so there is no retry, and no exposure fires.
				if (await treatExperiment(utilityContext, experiment.experimentName)) {
					setTreatedPetitionId(petitionId);
				}
			} catch (e: unknown) {
				// Backstop if treatExperiment's resolve-never-reject contract ever regresses — this must not be
				// silent, since it would drop $exposure for the whole experiment.
				void utilityContext.errorReporter.report({
					error:
						e instanceof Error
							? e
							: new Error('useTreatAddressPlaceholderExperiment: unexpected rejection from treatExperiment'),
					params: { experimentName: experiment.experimentName },
				});
			}
		})();
	}, [qualified, isRealAssignment, experiment.experimentName, petitionId, utilityContext]);

	/**
	 * `treatExperiment` registers treatment server-side but the resolved object stays `treated: false`, so
	 * it has to be flipped locally or `useTrackExperimentExposure` no-ops. `|| override` is needed because
	 * that hook returns on `!treated` before reaching its `override` branch, so a QA `?exo.` visit would
	 * otherwise get no "$exposure SUPPRESSED by override" log. No real exposure leaks: that branch logs and
	 * returns before sending.
	 *
	 * Unqualified visitors get `INERT_EXPERIMENT` rather than the real one, so `experiment.treated` from a
	 * visitor who never reached the address step can't leak an exposure the moment the normalizer stops
	 * forcing `treated: false`.
	 */
	const experimentForExposure = useMemo<AddressPlaceholderExperiment>(() => {
		if (!qualified) return INERT_EXPERIMENT;
		const treated = treatedPetitionId === petitionId || experiment.treated || Boolean(experiment.override);
		return treated ? { ...experiment, treated: true } : experiment;
	}, [qualified, experiment, treatedPetitionId, petitionId]);

	// `resetKeys` is required, not optional: variations are deterministic per user, so without the petition
	// id an SPA petition-to-petition navigation would dedup the new petition's legitimate exposure.
	const trackExperimentExposure = useTrackExperimentExposure(experimentForExposure, { resetKeys: [petitionId] });

	useEffect(() => {
		if (!experimentForExposure.treated) return;
		void trackExperimentExposure();
	}, [experimentForExposure.treated, trackExperimentExposure]);
}
